import React from "react";

import ExecutiveRecommendationPanel from "../../executive/ExecutiveRecommendationPanel";

import InsurancePolicyCard from "../coverage/InsurancePolicyCard";
import RiskFinancingKpis from "../shared/RiskFinancingKpis";
import RiskFinancingCurve from "../shared/RiskFinancingCurve";

import {
  formatMoney,
  formatMoneyFull,
  formatPercent,
  formatRatio,
} from "../../../utils/formatters";

export default function RiskFinancingOverview({
  summary,
  portfolioData,
  decisionData,
  strategy,
  settings,
  loading,
  error,
  onStrategyChange,
  onRecalculate,
  onReviewCoverage,
  onCompareAlternatives,
}) {
  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const executiveDecision =
    portfolioData?.executive_decision ||
    decisionData?.executive_decision ||
    null;

  return (
    <div className="space-y-6">
      <PortfolioExecutiveContext
        data={portfolioData}
        settings={settings}
      />

      <CurrentFinancingProgram
        policy={portfolioData?.policy}
        summary={summary}
        settings={settings}
      />

      <PortfolioFinancialPosition
        summary={summary}
        settings={settings}
      />

      <ExecutiveAssessment
        decision={executiveDecision}
        legacyInsight={portfolioData?.executive_insight}
        settings={settings}
      />

      <DecisionActions
        onReviewProgram={() =>
          scrollTo("portfolio-current-program")
        }
        onAdjustStrategy={() =>
          scrollTo("portfolio-candidate-strategy")
        }
        onCompareAlternatives={onCompareAlternatives}
        onReviewCoverage={onReviewCoverage}
      />

      <div
        id="portfolio-candidate-strategy"
        className="scroll-mt-6"
      >
        <InsurancePolicyCard
          strategy={strategy}
          settings={settings}
          onChange={onStrategyChange}
          onRecalculate={onRecalculate}
          loading={loading}
        />
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          Unable to update the portfolio financing analysis.
        </div>
      )}

      <section aria-labelledby="portfolio-results-title">
        <SectionHeading
          eyebrow="Financial Results"
          title="Candidate portfolio financing results"
          description="Expected losses are allocated across insurance transfer, organizational retention and exposure above the policy limit."
          id="portfolio-results-title"
        />

        <div className="mt-4">
          <RiskFinancingKpis
            summary={summary}
            settings={settings}
          />
        </div>
      </section>

      <RiskFinancingCurve
        data={portfolioData}
        strategy={strategy}
        settings={settings}
      />

      <RecommendationAndAlternatives
        decisionData={decisionData}
        fallbackDecision={executiveDecision}
        settings={settings}
        onCompareAlternatives={onCompareAlternatives}
      />
    </div>
  );
}

function PortfolioExecutiveContext({
  data,
  settings,
}) {
  const currency = settings?.currency || "USD";
  const baseline = data?.baseline || {};
  const scenariosCount = data?.scenarios_count;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <SectionHeading
        eyebrow="Portfolio Executive Context"
        title="Aggregate FAIR portfolio financing position"
        description="A consolidated view of how the active quantified risk portfolio is retained, transferred and left exposed under the current financing structure."
      />

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <ContextMetric
          label="Portfolio Scope"
          value={formatScope(data?.scope)}
          detail="Aggregate FAIR portfolio"
        />

        {scenariosCount !== undefined && (
          <ContextMetric
            label="Active FAIR Scenarios"
            value={String(scenariosCount)}
            detail="Included in this analysis"
          />
        )}

        {hasValue(baseline?.p95) && (
          <ContextMetric
            label="Portfolio P95 Exposure"
            value={formatMoney(
              baseline.p95,
              currency
            )}
            detail="Baseline loss exposure"
          />
        )}
      </div>
    </section>
  );
}

function CurrentFinancingProgram({
  policy = {},
  summary = {},
  settings,
}) {
  const currency = settings?.currency || "USD";

  return (
    <section
      id="portfolio-current-program"
      className="scroll-mt-6 rounded-2xl border border-slate-200 bg-white p-6"
    >
      <SectionHeading
        eyebrow="Current Portfolio Financing Program"
        title="Current insurance-transfer structure"
        description="The policy terms and resulting expected-loss allocation currently evaluated for the aggregate portfolio."
      />

      <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Metric label="Annual Premium" value={formatMoneyFull(policy?.premium, currency, 0)} />
        <Metric label="Deductible" value={formatMoneyFull(policy?.deductible, currency, 0)} />
        <Metric label="Policy Limit" value={formatMoneyFull(policy?.policy_limit, currency, 0)} />
        <Metric label="Coinsurance" value={formatPercent(normalizeRatio(policy?.coinsurance), 0)} />
        <Metric label="Transferred Loss" value={formatMoney(summary?.transferred_eal, currency)} tone="positive" />
        <Metric label="Retained Loss" value={formatMoney(summary?.retained_eal, currency)} />
        <Metric label="Residual Exposure" value={formatMoney(summary?.residual_eal, currency)} tone="negative" />
        <Metric label="Transfer Efficiency" value={formatRatio(summary?.transfer_efficiency)} />
      </div>
    </section>
  );
}

function PortfolioFinancialPosition({
  summary = {},
  settings,
}) {
  const currency = settings?.currency || "USD";
  const transferredRatio = normalizePercentage(
    summary?.transferred_pct
  );
  const retainedRatio = normalizePercentage(
    summary?.retained_pct
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <SectionHeading
          eyebrow="Portfolio Financial Position"
          title={`${formatMoney(summary?.total_eal, currency)} expected annual loss`}
          description="The current policy separates expected loss between insurer-funded recovery and risk retained by the organization."
        />

        <div className="rounded-xl bg-slate-50 px-4 py-3 text-right">
          <p className="text-xs font-medium text-slate-500">
            EAL Coverage Ratio
          </p>
          <p className="mt-1 text-3xl font-semibold text-slate-900">
            {formatPercent(transferredRatio, 1)}
          </p>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Metric label="Insurance Transfer" value={formatMoney(summary?.transferred_eal, currency)} tone="positive" />
        <Metric label="Company Retained Risk" value={formatMoney(summary?.retained_eal, currency)} />
        <Metric label="Residual Risk Exposure" value={formatMoney(summary?.residual_eal, currency)} tone="negative" />
      </div>

      <div className="mt-5 flex h-4 overflow-hidden rounded-full bg-slate-200">
        <div
          className="h-full bg-emerald-500"
          style={{ width: `${Math.min(transferredRatio * 100, 100)}%` }}
          title={`Transferred ${formatPercent(transferredRatio, 1)}`}
        />
        <div
          className="h-full bg-slate-400"
          style={{ width: `${Math.min(retainedRatio * 100, 100)}%` }}
          title={`Retained ${formatPercent(retainedRatio, 1)}`}
        />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-3">
        {hasValue(summary?.p95_loss) && (
          <PositionDetail label="P95 Gross Loss" value={formatMoney(summary.p95_loss, currency)} />
        )}
        {hasValue(summary?.p95_retained) && (
          <PositionDetail label="P95 Capital at Risk" value={formatMoney(summary.p95_retained, currency)} />
        )}
        <PositionDetail label="Insurance Efficiency" value={formatRatio(summary?.transfer_efficiency)} />
      </div>
    </section>
  );
}

function ExecutiveAssessment({
  decision,
  legacyInsight,
  settings,
}) {
  const currency = settings?.currency || "USD";
  const recommendation = decision?.recommended_strategy;

  if (!decision && legacyInsight) {
    return (
      <div aria-label="Executive Assessment">
        <ExecutiveRecommendationPanel
          label={legacyInsight.label}
          description={legacyInsight.description}
          recommendation={legacyInsight.recommendation}
          metricLabel={legacyInsight.metric_label}
          metricValue={legacyInsight.metric_value}
          tone={legacyInsight.tone || "info"}
        />
      </div>
    );
  }

  if (!decision) return null;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <SectionHeading
        eyebrow="Executive Assessment"
        title={recommendation?.strategy_name || "Backend financing decision"}
        description={decision.rationale || "The Executive Decision Contract does not provide additional rationale for this recommendation."}
      />

      <div className="mt-6 grid grid-cols-1 gap-4 md:grid-cols-3">
        <Metric label="Recommended Strategy" value={recommendation?.strategy_name || "—"} />
        {hasValue(recommendation?.total_financing_cost) && (
          <Metric label="Expected Financial Cost" value={formatMoney(recommendation.total_financing_cost, currency)} />
        )}
        {hasValue(recommendation?.tail_exposure) && (
          <Metric label="Residual Exposure" value={formatMoney(recommendation.tail_exposure, currency)} tone="negative" />
        )}
      </div>

      {decision.reason_codes?.length > 0 && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Backend Reason Codes
          </p>
          <p className="mt-2 text-sm font-medium text-slate-900">
            {decision.reason_codes.join(", ")}
          </p>
        </div>
      )}
    </section>
  );
}

function DecisionActions({
  onReviewProgram,
  onAdjustStrategy,
  onCompareAlternatives,
  onReviewCoverage,
}) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <SectionHeading
        eyebrow="Decision Actions"
        title="Move from position to evaluation"
        description="Review the current program, test revised terms, or continue into the existing comparison and coverage workflows."
      />

      <div className="mt-5 flex flex-wrap gap-2">
        <ActionButton onClick={onReviewProgram}>Review Current Program</ActionButton>
        <ActionButton onClick={onAdjustStrategy}>Adjust Financing Strategy</ActionButton>
        {onReviewCoverage && (
          <ActionButton onClick={onReviewCoverage}>Review Coverage Gaps</ActionButton>
        )}
        {onCompareAlternatives && (
          <ActionButton onClick={onCompareAlternatives} primary>Compare Alternatives</ActionButton>
        )}
      </div>
    </section>
  );
}

function RecommendationAndAlternatives({
  decisionData,
  fallbackDecision,
  settings,
  onCompareAlternatives,
}) {
  const currency = settings?.currency || "USD";
  const comparison = decisionData?.comparison;
  const decision =
    decisionData?.executive_decision ||
    fallbackDecision;
  const recommended =
    decision?.recommended_strategy ||
    comparison?.recommended_strategy;
  const strategies = Array.isArray(comparison?.strategies)
    ? comparison.strategies
    : [];
  const current = strategies.find(
    (item) => item?.strategy_name === "Current Policy"
  );

  if (!recommended) return null;

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <SectionHeading
          eyebrow="Recommendation and Alternatives"
          title={recommended.strategy_name || "Recommended financing strategy"}
          description={decision?.rationale || "Review the backend-evaluated strategy results before changing the current program."}
        />
        {onCompareAlternatives && (
          <ActionButton onClick={onCompareAlternatives} primary>
            Open Full Comparison
          </ActionButton>
        )}
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <StrategySummary title="Recommended Strategy" strategy={recommended} currency={currency} highlighted />
        {current && (
          <StrategySummary title="Current Program" strategy={current} currency={currency} />
        )}
      </div>
    </section>
  );
}

function StrategySummary({ title, strategy, currency, highlighted = false }) {
  return (
    <div className={`rounded-xl border p-4 ${highlighted ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{title}</p>
      <p className="mt-2 text-lg font-semibold text-slate-900">{strategy?.strategy_name || "—"}</p>
      <dl className="mt-4 grid grid-cols-2 gap-3 text-sm">
        <Definition label="Premium" value={formatMoney(strategy?.premium, currency)} />
        <Definition label="Policy Limit" value={formatMoney(strategy?.policy_limit, currency)} />
        <Definition label="Transferred" value={formatMoney(strategy?.transferred_loss, currency)} />
        <Definition label="Retained" value={formatMoney(strategy?.retained_loss, currency)} />
        <Definition label="Residual" value={formatMoney(strategy?.tail_exposure, currency)} />
        <Definition label="Total Cost" value={formatMoney(strategy?.total_financing_cost, currency)} />
      </dl>
    </div>
  );
}

function SectionHeading({ eyebrow, title, description, id }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">{eyebrow}</p>
      <h2 id={id} className="mt-2 text-xl font-semibold text-slate-900">{title}</h2>
      {description && <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">{description}</p>}
    </div>
  );
}

function ContextMetric({ label, value, detail }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className="mt-2 text-xl font-semibold text-slate-900">{value}</p>
      <p className="mt-1 text-xs text-slate-500">{detail}</p>
    </div>
  );
}

function Metric({ label, value, tone = "neutral" }) {
  const color = tone === "positive" ? "text-emerald-700" : tone === "negative" ? "text-red-700" : "text-slate-900";
  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <p className="text-xs font-medium text-slate-500">{label}</p>
      <p className={`mt-2 text-lg font-semibold ${color}`}>{value}</p>
    </div>
  );
}

function PositionDetail({ label, value }) {
  return <div className="rounded-lg bg-slate-50 px-4 py-3"><p className="text-xs text-slate-500">{label}</p><p className="mt-1 text-sm font-semibold text-slate-900">{value}</p></div>;
}

function Definition({ label, value }) {
  return <div><dt className="text-xs text-slate-500">{label}</dt><dd className="mt-1 font-medium text-slate-900">{value}</dd></div>;
}

function ActionButton({ children, onClick, primary = false }) {
  return (
    <button type="button" onClick={onClick} className={`rounded-lg px-3 py-2 text-sm font-medium ${primary ? "bg-slate-900 text-white hover:bg-slate-800" : "border border-slate-300 bg-white text-slate-700 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

function hasValue(value) {
  return value !== null && value !== undefined;
}

function normalizePercentage(value) {
  const number = Number(value ?? 0);
  return number > 1 ? number / 100 : number;
}

function normalizeRatio(value) {
  return Math.min(Math.max(Number(value ?? 0), 0), 1);
}

function formatScope(scope) {
  if (!scope) return "Portfolio";
  return String(scope)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
